<template>
  <div v-if="show">
    <base-popup @close="$emit('close')" :media="possibleCompositions" :gridSize="280">
      <template #header>
        <h3>Sélectionner une ou plusieurs compositions</h3>
      </template>
      <template #bodyContent v-if="possibleCompositions.length == 0">
        Aucune composition trouvée
      </template>
      <template #body="{el, index}">
        <div class="composition-item">
          <div class="composition-item-content" @click="toggleComposition(el)">
            <div class="composition-checkbox" >
              <div class="checkbox-inner" :class="{ 'checked': compositionCompare(el) }">
                <validateIcon v-if="compositionCompare(el)" class="checkbox-icon"/>
              </div>
            </div>
            <div class="composition-content">
              <div class="image-container">
                <div v-if="loadingImages[el.id] == true" class="image-loader">
                  <div class="loader-spinner"></div>
                </div>
                <img 
                  :src="el.imagePath" 
                  :alt="el.Name"
                  class="composition-image"
                  @load="onImageLoad(el.id)"
                />
              </div>
            </div>
          </div>
          <div class="composition-name">
            {{ el.Name }}
          </div>
          <div class="composition-footer">
            <button class="delete-button" @click="askDeleteComposition(el)" v-if="el.CanBeDeleted">
              <deleteIcon class="button-icon"/>
              Supprimer
            </button>
            <button class="edit-button" @click="duplicateComposition(el)">
              <copyIcon class="button-icon"/>
              Dupliquer
            </button>
            <button class="edit-button" @click="editComposition(el)">
              <editIcon class="button-icon"/>
              Éditer
            </button>
          </div>
        </div>
      </template>
      <template #footer>
          <button class="popup-button create" @click="editComposition()">
            <addIcon class="button-icon"/>
            Créer une composition
          </button>
          <div class="footer-actions">
            <button 
              class="popup-button validate" 
              @click="validateSelection"
            >
              <validateIcon class="button-icon"/>
              Valider
            </button>
          </div>
      </template>
    </base-popup>
    <delete-confirm-popup
      :show="showDeleteConfirmPopup"
      @close="showDeleteConfirmPopup = false"
      @confirm="deleteComposition(confirmDeleteElement)"
      :elementName="confirmDeleteElement?.name"/>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import * as namespaceStore from "../../vuexStore/namespaceStore.js"
import * as mutates from "../../vuexStore/mutations.js"
import * as actions from "../../vuexStore/actions.js"
import validateIcon from "../IconComponents/validateIcon.vue"
import cancelIcon from "../IconComponents/cancelIcon.vue"
import editIcon from "../IconComponents/editIcon.vue"
import deleteIcon from "../IconComponents/deleteIcon.vue"
import addIcon from "../IconComponents/addIcon.vue"
import copyIcon from "../IconComponents/copyIcon.vue"
import BasePopup from './Base/BasePopup.vue'
import DeleteConfirmPopup from "./DeleteConfirmPopup.vue"

export default {
  name: 'CompositionSelectionPopup',
  components: {
    validateIcon,
    cancelIcon,
    editIcon,
    addIcon,
    BasePopup,
    deleteIcon,
    DeleteConfirmPopup,
    copyIcon
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    }
  },
  data() {
    return {
      selectedCompositions: [],
      loadingImages: {},
      showDeleteConfirmPopup: false,
      confirmDeleteElement: null
    }
  },
  watch: {
    show: function() {
      if (this.show == true) {
        this.selectedCompositions = [...this.compositions];
        // Initialiser l'état de chargement pour toutes les images
        this.compositions.forEach(composition => {
            this.loadingImages[composition.id] = true;
        })
      }
    }
  },
  computed: {
    ...mapGetters(namespaceStore.THEME, [
      'possibleCompositions',
      'compositions'
    ])
  },
  emits: ['close', 'select'],
  methods: {
    ...mapMutations(namespaceStore.THEME, {
      setCompositions: mutates.SET_COMPOSITIONS,
    }),
    ...mapActions(namespaceStore.THEME, {
      removeComposition: actions.REMOVE_COMPOSITION,
      duplicateCompositionAction: actions.DUPLICATE_COMPOSITION
    }),
    askDeleteComposition(comp) {
      this.showDeleteConfirmPopup = true;
      this.confirmDeleteElement = comp;
    },
    deleteComposition(comp) {
      this.removeComposition(comp).then(() => {
        let index = this.selectedCompositions.findIndex(c => c.previewId == comp.previewId);
        if (index != -1) {
          this.selectedCompositions.splice(index, 1);
        }
      });
      this.showDeleteConfirmPopup = false;
    },
    duplicateComposition(comp) {
      this.duplicateCompositionAction(comp).then((comp) => {
        this.addMessage({
          message: 'Composition sauvegardée avec succès : ' + comp.Name,
          type: 'success'
        });
      }).catch((error) => {
        this.addMessage({
          message: 'Erreur lors de l\'upload de la composition : ' + comp.Name,
          type: 'error'
        });
      });
    },
    editComposition(comp=undefined) {
      if (comp == undefined) {
        this.$router.push({name: 'Composition'})
      } else {
        this.$router.push({name: 'Composition', params: {siteId: comp.siteId}})
      }
    },
    compositionCompare(comp) {
      return this.selectedCompositions.some(c => c.previewId == comp.previewId);
    },
    onImageLoad(compositionId) {
        this.loadingImages[compositionId] = false;
    },
    toggleComposition(comp) {
      const index = this.selectedCompositions.findIndex(c => c.previewId == comp.previewId);
      if (index === -1) {
        this.selectedCompositions.push(comp)
      } else {
        this.selectedCompositions.splice(index, 1)
      }
    },
    validateSelection() {
      this.setCompositions(this.selectedCompositions);
      this.$emit('close')
      this.selectedCompositions = []
    }
  }
}
</script>

<style scoped>
.composition-item-content {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 0.75rem;
  width: 100%;
}

.composition-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.composition-item:hover {
  background-color: rgba(59, 130, 246, 0.1);
  border-color: #3b82f6;
}

.composition-item.selected {
  background-color: rgba(59, 130, 246, 0.2);
  border-color: #3b82f6;
}

.composition-checkbox {
  width: 1.25rem;
  height: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.checkbox-inner {
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.checkbox-inner.checked {
  background-color: #3b82f6;
  border-color: #3b82f6;
}

.checkbox-icon {
  width: 0.75rem;
  height: 0.75rem;
  color: white;
}

.composition-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
}

.image-container {
  position: relative;
  width: 100%;
  height: 120px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 0.25rem;
  overflow: hidden;
}

.image-loader {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1;
}

.loader-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(59, 130, 246, 0.3);
  border-radius: 50%;
  border-top-color: #3b82f6;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.composition-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 0.25rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.composition-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
}

.composition-name {
  color: #e5e7eb;
  font-size: 1.05rem;
  text-align: center;
  width: 100%;
}

.edit-button, .delete-button {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-weight: 500;
  transition: all 0.2s ease;
  text-shadow: 0px 0px 3px black;
  font-size: 1rem;
}

.edit-button {
  background-color: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  color: #3b82f6;
}

.edit-button:hover {
  background-color: rgba(59, 130, 246, 0.2);
  border-color: #3b82f6;
}

.delete-button {
  background-color: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

.delete-button:hover {
  background-color: rgba(239, 68, 68, 0.2);
  border-color: #ef4444;
}


.footer-actions {
  display: flex;
  gap: 0.75rem;
}

.popup-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.popup-button.cancel {
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #e5e7eb;
}

.popup-button.cancel:hover {
  background-color: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
}

.popup-button.validate {
  background-color: rgba(59, 130, 246, 0.2);
  border: 1px solid rgba(59, 130, 246, 0.3);
  color: #3b82f6;
}

.popup-button.validate:hover:not(:disabled) {
  background-color: rgba(59, 130, 246, 0.3);
  border-color: #3b82f6;
}

.popup-button.validate:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.button-icon {
  width: 1rem;
  height: 1rem;
}

.popup-button.create {
  background-color: rgba(16, 185, 129, 0.2);
  border: 1px solid rgba(16, 185, 129, 0.3);
  color: #10b981;
}

.popup-button.create:hover {
  background-color: rgba(16, 185, 129, 0.3);
  border-color: #10b981;
}
</style> 